/**
 * The technician's offline queue.
 *
 * Every action is written to the phone first and shown as done immediately,
 * then sent when there is signal. In a cabinet, a POP room or out past Keumbu
 * the alternative is an error message and a form that loses what was typed.
 *
 * Correctness rests on one thing: each queued item carries a `clientActionId`
 * generated once, at the moment of the tap. Every retry of that tap sends the
 * same id, and the server replays the original response instead of doing the
 * work again. A queue that can duplicate a completion is worse than no queue,
 * because people would trust it.
 */

export const DB_NAME = 'gsn-field';
const DB_VERSION = 1;
const STORE = 'queue';

export type QueuedAction = {
  id: string;
  assignmentId: string;
  /** 'action' posts a transition; 'photo' uploads evidence. */
  kind: 'action' | 'photo';
  body: Record<string, unknown>;
  createdAt: number;
  attempts: number;
  lastError?: string;
};

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const request = run(transaction.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
  });
}

export function newActionId(): string {
  return crypto.randomUUID();
}

export async function enqueue(item: Omit<QueuedAction, 'createdAt' | 'attempts'>): Promise<void> {
  await tx('readwrite', (store) =>
    store.put({ ...item, createdAt: Date.now(), attempts: 0 } satisfies QueuedAction),
  );
}

export async function pending(): Promise<QueuedAction[]> {
  const all = await tx<QueuedAction[]>('readonly', (store) => store.getAll() as IDBRequest<QueuedAction[]>);
  // Oldest first: the order the technician did them is the order they happened.
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

export async function remove(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id) as unknown as IDBRequest<undefined>);
}

async function markFailed(item: QueuedAction, error: string): Promise<void> {
  await tx('readwrite', (store) =>
    store.put({ ...item, attempts: item.attempts + 1, lastError: error }),
  );
}

export type SyncOutcome = { sent: number; failed: number; remaining: number };

/**
 * Drains the queue in order.
 *
 * Stops at the first network failure rather than racing on: if there is no
 * signal for one item there is none for the next, and a later action must never
 * land before the earlier one it depends on — arriving before travelling makes
 * no sense to the state machine or to anyone reading the timeline.
 */
export async function sync(): Promise<SyncOutcome> {
  const items = await pending();
  let sent = 0;
  let failed = 0;

  for (const item of items) {
    const path =
      item.kind === 'photo'
        ? `/t/${item.assignmentId}/photos`
        : `/t/${item.assignmentId}/actions`;

    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...item.body, clientActionId: item.id }),
      });

      if (response.ok) {
        await remove(item.id);
        sent += 1;
        continue;
      }

      // A 4xx is the server's considered answer: the action is invalid and
      // resending it will never help. Drop it rather than retry forever, but
      // keep the reason so the technician is told rather than left guessing.
      if (response.status >= 400 && response.status < 500) {
        const body = (await response.json().catch(() => ({}))) as { message?: string | string[] };
        const message = Array.isArray(body.message) ? body.message.join(' ') : body.message;
        await remove(item.id);
        failed += 1;
        console.warn(`[queue] dropped ${item.kind}: ${message ?? response.status}`);
        continue;
      }

      // 5xx — the server is having a bad time. Try again later.
      await markFailed(item, `HTTP ${response.status}`);
      break;
    } catch (error) {
      await markFailed(item, (error as Error).message);
      // No signal. Everything after this would fail too.
      break;
    }
  }

  return { sent, failed, remaining: (await pending()).length };
}

/** Fires on reconnect and periodically, because 'online' lies more than it should. */
export function startAutoSync(onChange: (outcome: SyncOutcome) => void): () => void {
  let running = false;

  const attempt = async () => {
    if (running || !navigator.onLine) return;
    running = true;
    try {
      const outcome = await sync();
      if (outcome.sent || outcome.failed) onChange(outcome);
    } finally {
      running = false;
    }
  };

  window.addEventListener('online', () => void attempt());
  // navigator.onLine reports the network interface, not whether anything is
  // reachable — a phone showing one bar of 3G is "online" and still failing.
  const timer = window.setInterval(() => void attempt(), 15_000);
  void attempt();

  return () => {
    window.removeEventListener('online', () => void attempt());
    window.clearInterval(timer);
  };
}
